import { useTranslation } from "react-i18next";
import U2000BasicTable, { type U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import type { U2000TableColumn } from "../../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useNavigate } from "react-router-dom";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../../u2000Core/reducer/formReducer/U2000LUState";
import type { Criteria } from "../../../u2000Core/reducer/formReducer/U2000SearchState";
import { useU3134Context } from "../../U3134Context";
import {
  doExportExcel,
  isU3134InvoicedLine,
  luHandleAppiError,
  u3134ExtractCreatedLineIds,
  type U3134GenerateWipProgressData,
  type U3134WorkInProgressSearchRow,
} from "../../U3134Service";
import { NS, U3134ROLES } from "../../U3134";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { U3134WorkInProgressListToolBar } from "./U3134WorkInProgressListToolBar";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000QuickSearchListEffect } from "../../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useU2000ExportExcelHandlers } from "../../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU3134GenerateWipHandlers, useU3134GetGenerateWipJobHandlers, useU3134SearchHandlers } from "../../U3134UseApiHandlers";
import { useU2000WebSocketContext } from "../../../u2000Core/context/websocket/U2000WebSocketContext";
import {
  isNotificationMessage,
  isU3134GenerateWipProgressMessage,
  U3134_GENERATE_WIP_JOB_TYPE,
} from "../../../u2000Core/types/U2000WebSocketMessages";
import U3134GenerateWipProgressPanel from "../common/U3134GenerateWipProgressPanel";
import U3134GenerateWipRecapDialog from "../common/U3134GenerateWipRecapDialog";
import {
  U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE,
  U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE,
  U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT,
  defaultItemToDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  u3134InvoiceLookupItemToDisplay,
  u3134InvoiceLookupItemToSelectedDisplay,
  u3134InvoiceLookupItemToValue,
  useU3134FindEmployeeHandlers,
  useU3134FindInvoiceHandlers,
  useU3134FindProjectHandlers,
} from "../../U3134UseFindCriteriaHandlers";
import { u3134GetWorkInProgressListColumns } from "../../tools/U3134WorkInProgressListColumns";
import { generateTemporaryId } from "../../../u2000Core/tools/U2000IdUtils";

/** Colonne synthétique pour critère post-génération `id in …` (hors grille). */
const U3134_POST_GEN_ID_COLUMN: U2000TableColumn = {
  name: "id",
  label: "Id",
  type: "string",
  isCriteriaField: true,
  inGridField: false,
  isDefaultColumn: false,
  includeConditions: ["in"],
};

const U3134WorkInProgressList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3134Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const { on, off, isConnected } = useU2000WebSocketContext();
  const runGenerateWip = useU3134GenerateWipHandlers();
  const getGenerateWipJob = useU3134GetGenerateWipJobHandlers();
  const findProjectHandlers = useU3134FindProjectHandlers();
  const findInvoiceHandlers = useU3134FindInvoiceHandlers();
  const findEmployeeHandlers = useU3134FindEmployeeHandlers();
  const [isGeneratingWip, setIsGeneratingWip] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [progressData, setProgressData] = useState<U3134GenerateWipProgressData | null>(null);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [finalRecap, setFinalRecap] = useState<{ title: string; message: string; importance: "success" | "warning" | "error" } | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const hasWsProgressRef = useRef<boolean>(false);
  const endedRef = useRef<boolean>(false);
  const postGenAppliedRef = useRef<boolean>(false);
  const activeJobRef = useRef<string | null>(null);
  activeJobRef.current = activeJobId;

  const clearPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current);
    }
    pollTimerRef.current = null;
  }, []);

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT, {
      search: findProjectHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE, {
      search: findInvoiceHandlers.search,
      itemToValue: u3134InvoiceLookupItemToValue,
      itemToDisplay: u3134InvoiceLookupItemToDisplay,
      itemToSelectedDisplay: u3134InvoiceLookupItemToSelectedDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE, {
      search: findEmployeeHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    return () => {
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_PROJECT);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_INVOICE);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3134_FIND_EMPLOYEE);
    };
  }, [findProjectHandlers.search, findInvoiceHandlers.search, findEmployeeHandlers.search]);

  useEffect(() => {
    const listTitle = t("title", { ns: NS });
    const columnsDef = u3134GetWorkInProgressListColumns(t, i18n.language);
    searchStateHandlers.setColumns(columnsDef);
    gridStateHandlers.setColumns(columnsDef);
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers instables
  }, [t, i18n.language]);

  const handleSearch = useU3134SearchHandlers<U3134WorkInProgressSearchRow>({
    onSuccess: (searchResult: U2000SearchResult<U3134WorkInProgressSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  useU2000ExportExcelNotification();

  const refreshList = useCallback(() => {
    handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(1), U2000GridStateEnumPaginationDirection.NEXTPAGE);
  }, [gridStateHandlers, handleSearch, searchStateHandlers]);

  /** BL-0425 : après generateWip, filtrer la liste sur `id in createdLineIds`. */
  const applyCreatedLinesFilter = useCallback(
    async (jobId: string) => {
      if (postGenAppliedRef.current) return;
      postGenAppliedRef.current = true;
      try {
        const job = await getGenerateWipJob(jobId);
        const ids = u3134ExtractCreatedLineIds(job);
        if (ids.length === 0) {
          toast.info(t("msgGenerateWipNoLinesCreated", { ns: NS }));
          refreshList();
          return;
        }
        const csv = ids.join(",");
        const displayValue = t("lblGenerateWipCreatedLinesFilter", { count: ids.length, ns: NS });
        const condition = { name: "in", label: t("lblCndIn", { ns: "common" }), isRequiredValue: true };
        const criteria: Criteria = {
          id: generateTemporaryId(),
          field: U3134_POST_GEN_ID_COLUMN,
          condition,
          value: csv,
          displayValue,
          timestamps: Date.now(),
        };
        searchStateHandlers.setCriterias([criteria]);
        const base = searchStateHandlers.extractSearchParameter();
        handleSearch.handleSearch(
          {
            ...base,
            criterias: [{ field: "id", operator: "in", value: csv, type: "string" }],
          },
          gridStateHandlers.extractPagination(1),
          U2000GridStateEnumPaginationDirection.NEXTPAGE
        );
      } catch {
        refreshList();
      }
    },
    [getGenerateWipJob, gridStateHandlers, handleSearch, refreshList, searchStateHandlers, t, toast]
  );

  const pollJob = useCallback(
    async (jobId: string, delayMs: number = 1500) => {
      clearPolling();
      pollTimerRef.current = window.setTimeout(async () => {
        try {
          const job = await getGenerateWipJob(jobId);
          if (activeJobRef.current !== jobId || endedRef.current) return;
          setProgressPercent(typeof job.progressPercent === "number" ? job.progressPercent : null);
          if (job.progressData) {
            setProgressData(job.progressData);
          }
          if (job.status === "completed" || job.status === "failed" || job.status === "cancelled") {
            endedRef.current = true;
            setIsGeneratingWip(false);
            setActiveJobId(null);
            if (job.status === "completed") {
              void applyCreatedLinesFilter(jobId);
            } else {
              refreshList();
            }
            return;
          }
          void pollJob(jobId, Math.min(5000, Math.round(delayMs * 1.4)));
        } catch {
          void pollJob(jobId, Math.min(6000, Math.round(delayMs * 1.5)));
        }
      }, delayMs);
    },
    [applyCreatedLinesFilter, clearPolling, getGenerateWipJob, refreshList]
  );

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3134",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  const u3134ListHandlerActions: U2000TableHandlers<U3134WorkInProgressSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3134WorkInProgressSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onModify: () => {
      const row = gridStateHandlers.getSelectedRows()[0];
      if (isU3134InvoicedLine(row)) {
        toast.info(t("msgInvoicedLineNotEditable", { ns: NS }));
        return;
      }
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3134");
      navigate(`${row?.id}`);
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
    onSpecialAction: async () => {
      if (isGeneratingWip) return;
      try {
        setIsGeneratingWip(true);
        endedRef.current = false;
        hasWsProgressRef.current = false;
        postGenAppliedRef.current = false;
        setProgressPercent(0);
        setProgressData(null);
        setFinalRecap(null);
        const job = await runGenerateWip();
        const jobId = job.id;
        setActiveJobId(jobId);
        if (typeof job.progressPercent === "number") {
          setProgressPercent(job.progressPercent);
        }
        if (job.progressData) {
          setProgressData(job.progressData as U3134GenerateWipProgressData);
        }
        if (!isConnected) {
          void pollJob(jobId);
        } else {
          window.setTimeout(() => {
            if (!hasWsProgressRef.current && !endedRef.current && activeJobRef.current === jobId) {
              void pollJob(jobId);
            }
          }, 4500);
        }
      } catch (error: unknown) {
        setIsGeneratingWip(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
        const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      }
    },
  };

  useEffect(() => {
    const handleProgress = (message: unknown) => {
      if (!activeJobRef.current) return;
      if (!isU3134GenerateWipProgressMessage(message)) return;
      if (message.jobId !== activeJobRef.current) return;
      if (message.jobType && message.jobType !== U3134_GENERATE_WIP_JOB_TYPE) return;
      hasWsProgressRef.current = true;
      setProgressData({
        phase: message.phase,
        current: message.current,
        total: message.total,
        currentItem: message.currentItem,
        lastResult: message.lastResult,
        counters: message.counters,
      });
      if (typeof message.current === "number" && typeof message.total === "number" && message.total > 0) {
        setProgressPercent(Math.min(100, Math.max(0, Math.round((message.current / message.total) * 100))));
      }
    };

    on("job:progress", handleProgress);
    return () => off("job:progress", handleProgress);
  }, [on, off]);

  useEffect(() => {
    const handleNotification = (message: unknown) => {
      if (!activeJobRef.current) return;
      if (!isNotificationMessage(message)) return;
      const metadata = message.metadata as Record<string, unknown> | undefined;
      const metadataJobType = metadata?.jobType as string | undefined;
      if (metadataJobType && metadataJobType !== U3134_GENERATE_WIP_JOB_TYPE) return;

      const jobId = activeJobRef.current;
      endedRef.current = true;
      setIsGeneratingWip(false);
      clearPolling();
      const recapImportance = message.importance === "error" || message.importance === "critical" ? "error" : message.importance === "warning" ? "warning" : "success";
      setFinalRecap({
        title: message.title,
        message: message.message,
        importance: recapImportance,
      });
      setActiveJobId(null);
      void applyCreatedLinesFilter(jobId);
    };

    on("notification:new", handleNotification);
    return () => off("notification:new", handleNotification);
  }, [applyCreatedLinesFilter, clearPolling, off, on]);

  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- toolbar aligné U3106
          <U3134WorkInProgressListToolBar
            handlerActions={u3134ListHandlerActions as any}
            isGeneratingWip={isGeneratingWip}
          />
        }
        handlerActions={u3134ListHandlerActions}
        label={t("lblWorkInProgressList", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3134ROLES.EXPORT)}
      />
      <U3134GenerateWipProgressPanel
        isOpen={isGeneratingWip || !!activeJobId}
        progressPercent={progressPercent}
        progressData={progressData}
        onClose={() => {
          setIsGeneratingWip(false);
        }}
      />
      <U3134GenerateWipRecapDialog
        isOpen={!!finalRecap}
        recap={finalRecap}
        progressData={progressData}
        onClose={() => setFinalRecap(null)}
      />
    </>
  );
};

export default U3134WorkInProgressList;
